import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import AddIcon from '@mui/icons-material/AddOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { PERMISSIONS } from '@/types/auth';
import SupplierFormDialog from './SupplierFormDialog';
import type { SupplierFormValues } from './SupplierFormDialog';
import type { CreateSupplierRequest, SupplierDto, UpdateSupplierRequest } from '@/types/materials';

export default function SupplierMaster() {
  const { t } = useTranslation('supplierMaster');
  const queryClient = useQueryClient();

  const { data: config } = useProcessConfiguration();
  const { data: suppliers = [], isLoading } = useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => (await apiClient.get<SupplierDto[]>('/suppliers')).data,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<SupplierDto | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<SupplierDto | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['suppliers'] });

  const saveMutation = useMutation({
    mutationFn: (values: SupplierFormValues) => {
      if (editingSupplier) {
        const payload: UpdateSupplierRequest = values;
        return apiClient.put(`/suppliers/${editingSupplier.id}`, payload);
      }
      const payload: CreateSupplierRequest = values;
      return apiClient.post('/suppliers', payload);
    },
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      setEditingSupplier(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/suppliers/${id}`),
    onSuccess: () => {
      invalidate();
      setConfirmDelete(null);
    },
  });

  const columns: DataTableColumn<SupplierDto>[] = [
    { field: 'code', headerName: t('table.code') },
    { field: 'name', headerName: t('table.name') },
    { field: 'gstin', headerName: t('table.gstin'), render: (r) => r.gstin ?? '—' },
    { field: 'contactPerson', headerName: t('table.contactPerson'), render: (r) => r.contactPerson ?? '—' },
    { field: 'phone', headerName: t('table.phone'), render: (r) => r.phone ?? '—' },
    ...(config?.jobWorkEnabled
      ? [{
          field: 'isJobWorkVendor',
          headerName: t('table.jobWorkVendor'),
          render: (r: SupplierDto) => (r.isJobWorkVendor ? t('table.jobWorkVendorYes') : '—'),
          filterable: true,
        } as DataTableColumn<SupplierDto>]
      : []),
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      render: (r) => (
        <IfPermitted permission={PERMISSIONS.SUPPLIER_MANAGE}>
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            <Button
              size="small"
              onClick={() => {
                setEditingSupplier(r);
                setDialogOpen(true);
              }}
            >
              {t('edit')}
            </Button>
            <Button size="small" color="error" onClick={() => setConfirmDelete(r)}>
              {t('delete')}
            </Button>
          </Stack>
        </IfPermitted>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.SUPPLIER_MANAGE}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingSupplier(null);
                setDialogOpen(true);
              }}
            >
              {t('addSupplier')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={suppliers} loading={isLoading} searchable searchPlaceholder={t('search')} getRowId={(r) => r.id} exportFileName="suppliers" />

      <SupplierFormDialog
        open={dialogOpen}
        supplier={editingSupplier}
        onClose={() => {
          setDialogOpen(false);
          setEditingSupplier(null);
        }}
        onSubmit={(values) => saveMutation.mutate(values)}
        loading={saveMutation.isPending}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        message={t('deleteSupplier.message')}
        loading={deleteMutation.isPending}
        onConfirm={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
        onClose={() => setConfirmDelete(null)}
      />
    </>
  );
}
